import React from 'react'
import { tokens } from "../../../theme";
import { Box, useTheme, Typography, FormControlLabel, Checkbox, Button } from "@mui/material";
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '../../../components/alert/Alert';
import Header from "../../../components/Header";
import Axios from 'axios';
import { useOutletContext } from 'react-router-dom';
import Loading from '../../../components/Loading';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useSignOut } from "react-auth-kit";
import { useNavigate } from "react-router-dom";
import getCookies from '../../../utils/getCookies';
import AdminActionAccordion from './AdminActionAccordion';
import AdminActionAccordionV2 from './AdminActionAccordionV2';
import TestBox from './TestBox';
import TestBoxV2 from './TestBoxV2';
import EttExamBox from '../homework/EttExamBox';
import ReorderCourseTestsModal from './ReorderCourseTestsModal';
import { getAuthHeader } from '../../../utils/authToken';
import v2Api from '../../../utils/v2Api';


const Test = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    // for 401 error
    const signOut = useSignOut();
    const navigate = useNavigate();
    const handleLogout = () => {
        signOut();
        localStorage.removeItem("userName");
        localStorage.removeItem("who");
        navigate("/signin");
    }

    const matches = useMediaQuery('(min-width:1500px)');
    const matches2 = useMediaQuery('(min-width:1200px)');

    const [loading, setLoading] = React.useState(true);
    const [courseId, setCourseId] = React.useState(null);
    const courseData = useOutletContext().courseData;
    const [students, setStudents] = React.useState(null);
    const [studentsStartEndDateMap, setStudentsStartEndDateMap] = React.useState(null);
    const [authorDisabledForAdminActions, setAuthorDisabledForAdminActions] = React.useState(false);
    const currentUser = getCookies("_auth_state");
    const userId = currentUser ? currentUser.split(',')[0]?.split(':')[1]?.split('"')[1] : null;
    const userRole = currentUser ? currentUser.split(',')[2]?.split(':')[1]?.split('"')[1] : null;
    const [studentId, setStudentId] = React.useState(null);
    const [tests, setTests] = React.useState([]);
    const [courseTests, setCourseTests] = React.useState([]);
    const [v2CourseTests, setV2CourseTests] = React.useState([]);
    const [prepExams, setPrepExams] = React.useState([]); // prep-program practice tests (also on Homework)
    const [hideLegacy, setHideLegacy] = React.useState(true);

    // for backdrop
    const [backdropOpen, setBackdropOpen] = React.useState(false);
    const handleBackdropClose = () => {
        setBackdropOpen(false);
    };

    // for alert
    const [successAlertOpen, setSuccessAlertOpen] = React.useState(false);
    const [errorAlertOpen, setErrorAlertOpen] = React.useState(false);
    const [successAlertMessage, setSuccessAlertMessage] = React.useState("");
    const [errorAlertMessage, setErrorAlertMessage] = React.useState("");

    const fetchV2CourseTests = React.useCallback(async () => {
        const numericCourseId = courseData?.id;
        if (!numericCourseId) return;
        try {
            const res = await v2Api.get(`/courses/${numericCourseId}/tests`);
            setV2CourseTests(res.data.data || []);
        } catch (err) {
            if (err.response?.status !== 404) console.log(err);
        }
    }, [courseData?.id]);

    // Clear the manual order → tests fall back to the default (due_date DESC).
    const handleResetOrder = async () => {
        if (!courseData?.id) return;
        setBackdropOpen(true);
        try {
            await v2Api.put(`/courses/${courseData.id}/tests/reset-order`);
            setSuccessAlertMessage("Order reset — sorted by due date");
            setSuccessAlertOpen(true);
            await fetchV2CourseTests();
        } catch (err) {
            setErrorAlertMessage(err.response?.data?.message || "Couldn't reset the order. Try again.");
            setErrorAlertOpen(true);
        } finally {
            setBackdropOpen(false);
        }
    };

    // useEffect
    React.useEffect(() => {
        localStorage.setItem('selectedMenu', 'Tests');
        setCourseId(window.location.pathname.split('/')[2]);

        if (userRole === "student" || userRole === "teacher")
            setAuthorDisabledForAdminActions(true);

        async function getStudents() {
            await Axios.get(`${process.env.REACT_APP_URL}/api/v1/courses/${window.location.pathname.split('/')[2]}/students`, {
                headers: {
                    Authorization: getAuthHeader(),
                },
            }).then((res) => {
                const all_students = [];
                for (let i = 0; i < res.data.length; i++) {
                    all_students.push(res.data[i].student);
                    setStudentsStartEndDateMap((prev) => ({
                        ...prev,
                        [res.data[i].student.id]: {
                            start_date: res.data[i].start_date,
                            end_date: res.data[i].end_date,
                        }
                    }));
                }

                setStudents(all_students);
            }).catch((err) => {
                if (err.response?.status === 401)
                    handleLogout();
                else if (err.response?.status !== 404)
                    console.log(err);
            });
        }

        async function getStudentId() {
            await Axios.get(`${process.env.REACT_APP_URL}/api/v1/students/${userId}`, {
                headers: {
                    Authorization: getAuthHeader(),
                },
            }).then((res) => {
                setStudentId(res.data.id);
            }).catch((err) => {
                if (err.response?.status === 401)
                    handleLogout();
                else if (err.response?.status !== 404)
                    console.log(err);
            });
        }

        async function getTest() {
            await Axios.get(`${process.env.REACT_APP_URL}/api/v1/tests`, {
                headers: {
                    Authorization: getAuthHeader(),
                },
            }).then((res) => {
                setTests(res.data);
            }).catch((err) => {
                if (err.response?.status === 401)
                    handleLogout();
                else if (err.response?.status !== 404)
                    console.log(err);
            });
        }

        async function getCourseTests() {
            await Axios.get(`${process.env.REACT_APP_URL}/api/v1/courses/${window.location.pathname.split('/')[2]}/tests`, {
                headers: {
                    Authorization: getAuthHeader(),
                },
            }).then((res) => {
                setCourseTests(res.data);
            }).catch((err) => {
                if (err.response?.status === 401)
                    handleLogout();
                else if (err.response?.status !== 404)
                    console.log(err);
            });
        }

        // Prep-program practice tests. They always show on the Tests page (in their own
        // "Practice Tests" section, below the course tests); the program's
        // show_exams_on_homework flag only governs the Homework page, so it's ignored
        // here. Students get their personal view (student_uuid); teachers/admins get the
        // per-student roster.
        async function getPrepItems() {
            await Axios.get(`${process.env.REACT_APP_URL}/api/v1/courses/${window.location.pathname.split('/')[2]}/prep-items`, {
                headers: { Authorization: getAuthHeader() },
                params: userRole === "student" ? { student_uuid: userId } : {},
            }).then((res) => {
                setPrepExams(res.data?.exams || []);
            }).catch((err) => {
                if (err.response?.status === 401) { handleLogout(); return; }
                if (err.response?.status !== 404) console.log(err);
            });
        }

        if (userRole === "student")
            getStudentId();

        getStudents();
        getTest();
        getCourseTests();
        getPrepItems();
        setLoading(false);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch v2 course tests when courseData.id becomes available
    React.useEffect(() => {
        if (courseData?.id) fetchV2CourseTests();
    }, [courseData?.id, fetchV2CourseTests]);

    return (
        <Box m="20px" marginX={3} marginY={3} minHeight="90%">
            { loading || courseData === null  ?
                <Loading />
                : <>
                    <Box 
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            px: "25%",
                            ...!matches && {
                                px: "15%",
                            },
                            ...!matches2 && {
                                px: "10%",
                            },
                        }}
                    >
                        <Header 
                            title={
                                courseData.code !== null 
                                ? courseData.code + ". " + courseData.course_title 
                                : courseData.course_title
                            } 
                            subtitle="A ONE Institute"
                        />
                        {/* For Admin */}
                        {userRole !== "student" && 
                            <Box
                                minWidth="20rem"
                                width="100%"
                                minHeight="10rem"
                                borderRadius="3px"
                                padding={3}
                                boxShadow="0px 0px 10px 10px rgba(0, 0, 0, 0.10)"
                                backgroundColor={colors.primary[600]}
                                display="flex"
                                flexDirection="column"
                                alignItems="left"
                                gap={3}
                                mb={3}
                            >
                                <Typography variant="h4" color={colors.primary[450]}>
                                    Admin Actions
                                </Typography>
                                <AdminActionAccordionV2
                                    courseId={courseData?.id}
                                    students={students}
                                    userRole={userRole}
                                    v2CourseTests={v2CourseTests}
                                    studentsStartEndDateMap={studentsStartEndDateMap}
                                    setBackdropOpen={setBackdropOpen}
                                    setSuccessAlertOpen={setSuccessAlertOpen}
                                    setErrorAlertOpen={setErrorAlertOpen}
                                    setSuccessAlertMessage={setSuccessAlertMessage}
                                    setErrorAlertMessage={setErrorAlertMessage}
                                    onRefresh={fetchV2CourseTests}
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={hideLegacy}
                                            onChange={(e) => setHideLegacy(e.target.checked)}
                                        />
                                    }
                                    label={<Typography variant="body2" color="text.secondary">Hide legacy test assigner</Typography>}
                                />
                                {!hideLegacy &&
                                    <AdminActionAccordion
                                        courseId={courseId}
                                        students={students}
                                        userRole={userRole}
                                        courseTests={courseTests}
                                        studentsStartEndDateMap={studentsStartEndDateMap}
                                        authorDisabledForAdminActions={authorDisabledForAdminActions}
                                        setBackdropOpen={setBackdropOpen}
                                        setSuccessAlertOpen={setSuccessAlertOpen}
                                        setErrorAlertOpen={setErrorAlertOpen}
                                        setSuccessAlertMessage={setSuccessAlertMessage}
                                        setErrorAlertMessage={setErrorAlertMessage}
                                        tests={tests}
                                    />
                                }
                                {userRole === "admin" && v2CourseTests.length > 1 &&
                                    <>
                                        <ReorderCourseTestsModal
                                            courseId={courseData?.id}
                                            courseTests={v2CourseTests}
                                            onReorder={fetchV2CourseTests}
                                            setSuccessAlertOpen={setSuccessAlertOpen}
                                            setErrorAlertOpen={setErrorAlertOpen}
                                            setSuccessAlertMessage={setSuccessAlertMessage}
                                            setErrorAlertMessage={setErrorAlertMessage}
                                            setBackdropOpen={setBackdropOpen}
                                        />
                                        {v2CourseTests.some((t) => t.order !== null && t.order !== undefined) &&
                                            <Button size="small" variant="outlined" sx={{ ml: 1 }} onClick={handleResetOrder}>
                                                Reset to due date
                                            </Button>
                                        }
                                    </>
                                }
                            </Box>
                        }
                        {/* For Students and teachers */}
                        <Box
                            minWidth="20rem"
                            width="100%"
                            minHeight="100%"
                            borderRadius="3px"
                            padding={8}
                            boxShadow="0px 0px 10px 10px rgba(0, 0, 0, 0.10)"
                            backgroundColor={colors.primary[600]}
                            alignItems="center"
                        >
                            {(v2CourseTests.length > 0 || prepExams.length > 0) ?
                                <>
                                    {/* Regular course tests — v2CourseTests arrives in the server's
                                        order (order ASC), preserving the admin's manual Reorder. */}
                                    {v2CourseTests.map((ct) => (
                                        <TestBoxV2
                                            key={`v2-${ct.id}`}
                                            courseTest={ct}
                                            courseId={courseId}
                                            students={students}
                                            studentsStartEndDateMap={studentsStartEndDateMap}
                                            userRole={userRole}
                                            userId={userId}
                                            studentId={studentId}
                                            setBackdropOpen={setBackdropOpen}
                                            setSuccessAlertOpen={setSuccessAlertOpen}
                                            setErrorAlertOpen={setErrorAlertOpen}
                                            setSuccessAlertMessage={setSuccessAlertMessage}
                                            setErrorAlertMessage={setErrorAlertMessage}
                                        />
                                    ))}

                                    {/* Prep-program practice tests — their own section below the
                                        course tests. Sorted by due date DESC to match the Homework
                                        page (where these exams fall to its due_date-DESC ordering). */}
                                    {prepExams.length > 0 &&
                                        <Box sx={{ mt: v2CourseTests.length > 0 ? "1.5rem" : 0 }}>
                                            <Typography variant="h5" sx={{ fontWeight: "bold", mb: "1rem", pb: "0.5rem", borderBottom: "0.1px solid #a3a3a3" }}>
                                                ETT Exams
                                            </Typography>
                                            {[...prepExams]
                                                .sort((a, b) => new Date(b.due_date) - new Date(a.due_date))
                                                .map((ex) => (
                                                    <EttExamBox key={`ett-exam-${ex.ett_exam_id}`} item={ex} />
                                                ))}
                                        </Box>
                                    }
                                </>
                                :
                                <Box sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    minWidth: "20rem",
                                    minHeight: "32rem",
                                    gap: "0.4rem",
                                }}>
                                    <InfoOutlinedIcon sx={{ my: "auto" }} />
                                    <Typography variant="h4" color={colors.primary[450]} my="auto">
                                        No test yet!
                                    </Typography>
                                </Box>
                            }
                        </Box>
                    </Box>
                    <Alert
                        successAlertOpen={successAlertOpen}
                        setSuccessAlertOpen={setSuccessAlertOpen}
                        errorAlertOpen={errorAlertOpen}
                        setErrorAlertOpen={setErrorAlertOpen}
                        successMsg={successAlertMessage}
                        errorMsg={errorAlertMessage}
                        vertical="bottom"
                        horizontal="left"
                    />
                    <Backdrop
                        sx={{ color: colors.primary[400], zIndex: 2147483646 }}
                        open={backdropOpen}
                        onClick={handleBackdropClose}
                    >
                        <CircularProgress color="inherit" />
                    </Backdrop>
                </>
            }
        </Box>
    )
}

export default Test;